import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Container,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  History as HistoryIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ParkingTestViewModel } from '../../viewmodels/ParkingTestViewModel';
import { touchFriendly, responsiveSpacing } from '../../styles/responsive';
import { GRADIENTS } from '../../styles/theme';
import { ParkingTestViewProps } from './types';
import { useLearningData } from './hooks/useLearningData';
import { useLearningWorkflow } from './hooks/useLearningWorkflow';
import { useTestSettings } from './hooks/useTestSettings';
import { ParkingTestHeader } from './components/ParkingTestHeader';
import { ParkingTestPanel } from './components/ParkingTestPanel';
import { DesktopHistoryTab, MobileHistoryDialog } from './components/ParkingHistoryTab';
import '../../index.css';

export const ParkingTestView: React.FC<ParkingTestViewProps> = ({ project, onBack }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [state, setState] = useState(ParkingTestViewModel.getInitialState());
  const [activeTab, setActiveTab] = useState(0);

  const {
    availableFolders,
    historyLoading,
    loadLearningHistory,
  } = useLearningData({ projectId: project.id, setState });

  const {
    selectedHistory,
    historyResultLoading,
    handleStartLearning,
    handleHistorySelect,
  } = useLearningWorkflow({
    projectId: project.id,
    state,
    setState,
    loadLearningHistory,
  });

  const {
    dataSelectionExpanded,
    setDataSelectionExpanded,
    testSettingsExpanded,
    setTestSettingsExpanded,
    historyDialogOpen,
    setHistoryDialogOpen,
    settingsDialogOpen,
    setSettingsDialogOpen,
  } = useTestSettings();

  const handleTabChange = useCallback(async (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    if (newValue === 1) {
      await loadLearningHistory();
    }
  }, [loadLearningHistory]);

  const handleSnackbarClose = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <ParkingTestHeader
        projectName={project.name}
        onBack={onBack || (() => navigate(-1))}
        onOpenSettings={() => setSettingsDialogOpen(true)}
        onOpenHistory={() => setHistoryDialogOpen(true)}
      />

      <Container maxWidth="xl" sx={{ ...responsiveSpacing.pagePadding, pb: { xs: 8, md: 3 } }}>

        {/* Tabs - Desktop only */}
        {!isMobile && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={activeTab} onChange={handleTabChange}>
              <Tab label="학습 시작하기" icon={<PlayIcon />} iconPosition="start" />
              <Tab label="학습 히스토리" icon={<HistoryIcon />} iconPosition="start" />
            </Tabs>
          </Box>
        )}

        {/* Mobile: Always show learning tab */}
        {isMobile && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
              학습 시작하기
            </Typography>
          </Box>
        )}

        {/* Learning content */}
        {(isMobile || activeTab === 0) && (
          <ParkingTestPanel
            state={state}
            setState={setState}
            availableFolders={availableFolders}
            dataSelectionExpanded={dataSelectionExpanded}
            setDataSelectionExpanded={setDataSelectionExpanded}
            testSettingsExpanded={testSettingsExpanded}
            setTestSettingsExpanded={setTestSettingsExpanded}
            onStartLearning={handleStartLearning}
            projectId={project.id}
          />
        )}

        {/* Desktop History Tab */}
        {!isMobile && activeTab === 1 && (
          <DesktopHistoryTab
            state={state}
            selectedHistory={selectedHistory}
            historyLoading={historyLoading}
            historyResultLoading={historyResultLoading}
            onHistorySelect={handleHistorySelect}
            projectId={project.id}
          />
        )}

        {/* Mobile Settings Dialog */}
        <MobileSettingsDialog
          open={settingsDialogOpen}
          onClose={() => setSettingsDialogOpen(false)}
          state={state}
          setState={setState}
          availableFolders={availableFolders}
          isSmallMobile={isSmallMobile}
          onStartLearning={() => {
            handleStartLearning();
            setSettingsDialogOpen(false);
          }}
        />

        {/* Mobile History Dialog */}
        <MobileHistoryDialog
          open={historyDialogOpen}
          onClose={() => setHistoryDialogOpen(false)}
          state={state}
          selectedHistory={selectedHistory}
          historyLoading={historyLoading}
          historyResultLoading={historyResultLoading}
          onHistorySelect={handleHistorySelect}
          projectId={project.id}
        />

        <Snackbar
          open={!!state.error}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity="error"
            sx={{
              width: '100%',
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
            elevation={6}
          >
            {state.error}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

// --- Mobile Settings Dialog (kept here as it binds state from multiple hooks) ---

interface MobileSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  state: ReturnType<typeof ParkingTestViewModel.getInitialState>;
  setState: React.Dispatch<React.SetStateAction<ReturnType<typeof ParkingTestViewModel.getInitialState>>>;
  availableFolders: { learning: string[]; test: string[]; roi: string[] };
  isSmallMobile: boolean;
  onStartLearning: () => void;
}

const MobileSettingsDialog: React.FC<MobileSettingsDialogProps> = ({
  open,
  onClose,
  state,
  setState,
  availableFolders,
  isSmallMobile,
  onStartLearning,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isSmallMobile}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">학습 설정</Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {/* Data Selection */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
            데이터 선택
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>학습 이미지 폴더</InputLabel>
              <Select
                value={state.selectedLearningFolder}
                label="학습 이미지 폴더"
                onChange={(e) => setState(prev => ({ ...prev, selectedLearningFolder: e.target.value }))}
              >
                {availableFolders.learning.map((folder) => (
                  <MenuItem key={folder} value={folder}>
                    {folder}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>ROI 파일</InputLabel>
              <Select
                value={state.selectedRoiFile}
                label="ROI 파일"
                onChange={(e) => setState(prev => ({ ...prev, selectedRoiFile: e.target.value }))}
              >
                {availableFolders.roi.map((file) => (
                  <MenuItem key={file} value={file}>
                    {file}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>테스트 이미지 폴더</InputLabel>
              <Select
                value={state.selectedTestFolder}
                label="테스트 이미지 폴더"
                onChange={(e) => setState(prev => ({ ...prev, selectedTestFolder: e.target.value }))}
              >
                {availableFolders.test.map((folder) => (
                  <MenuItem key={folder} value={folder}>
                    {folder}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Test Settings */}
        <Box>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
            테스트 설정
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Var Threshold"
              type="number"
              value={state.varThreshold}
              onChange={(e) => setState(prev => ({ ...prev, varThreshold: Number(e.target.value) }))}
              fullWidth
              size="small"
            />
            <TextField
              label="Learning Rate"
              type="number"
              value={state.learningRate}
              onChange={(e) => setState(prev => ({ ...prev, learningRate: Number(e.target.value) }))}
              fullWidth
              size="small"
              inputProps={{ step: 0.0001 }}
            />
            <TextField
              label="Iterations"
              type="number"
              value={state.iterations}
              onChange={(e) => setState(prev => ({ ...prev, iterations: Number(e.target.value) }))}
              fullWidth
              size="small"
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onClose}
          sx={{ ...touchFriendly.button }}
        >
          닫기
        </Button>
        <Button
          variant="contained"
          onClick={onStartLearning}
          disabled={state.loading || !state.selectedLearningFolder || !state.selectedRoiFile || !state.selectedTestFolder}
          startIcon={<PlayIcon />}
          sx={{ ...touchFriendly.button }}
        >
          학습 시작
        </Button>
      </DialogActions>
    </Dialog>
  );
};
